<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PlaygroundChat from '../components/PlaygroundChat.vue';
import TraceTimeline from '../components/TraceTimeline.vue';
import StudioBadge from '../components/StudioBadge.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const messages = useMapGetter('aiBotPlayground/getMessages');
const trace = useMapGetter('aiBotPlayground/getTrace');
const uiFlags = useMapGetter('aiBotPlayground/getUIFlags');

const botId = computed(() => route.params.botId);

const botName = computed(() => props.bot.name || '');

const handoffBadge = computed(() => {
  if (!trace.value?.handoff) return '';
  return t('AI_AGENT.PLAYGROUND.HANDOFF_BADGE', {
    reason: trace.value.handoff_reason,
  });
});

const send = content => {
  store.dispatch('aiBotPlayground/send', { botId: botId.value, content });
};

const reset = () => {
  store.dispatch('aiBotPlayground/reset', { botId: botId.value });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="max-w-2xl text-body-main text-n-slate-11">
        {{ $t('AI_AGENT.PLAYGROUND.DESCRIPTION') }}
      </p>
      <Button
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-rotate-ccw"
        :label="
          uiFlags.isResetting
            ? $t('AI_AGENT.PLAYGROUND.RESETTING')
            : $t('AI_AGENT.PLAYGROUND.RESET')
        "
        :is-loading="uiFlags.isResetting"
        :disabled="uiFlags.isSending"
        @click="reset"
      />
    </div>
    <div
      v-if="handoffBadge"
      class="flex items-center gap-2 px-3 py-2 rounded-lg text-body-main bg-n-amber-9/10 text-n-amber-11"
      data-testid="playground-handoff-badge"
    >
      <span class="i-lucide-life-buoy size-4 shrink-0" />
      {{ handoffBadge }}
    </div>
    <div class="grid items-start grid-cols-1 gap-5 lg:grid-cols-5">
      <section
        class="flex flex-col overflow-hidden lg:col-span-3 h-[36rem] rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <header
          class="flex items-center gap-3 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
        >
          <div class="relative shrink-0">
            <Avatar
              :name="botName"
              :src="bot.thumbnail"
              :size="32"
              rounded-full
            />
            <span
              class="absolute bottom-0 right-0 rounded-full size-2.5 bg-n-teal-9 ring-2 ring-n-solid-2"
            />
          </div>
          <div class="flex flex-col min-w-0">
            <h3 class="truncate text-heading-3 text-n-slate-12">
              {{ botName }}
            </h3>
            <span class="text-label-small text-n-slate-10">
              {{
                uiFlags.isSending
                  ? $t('AI_AGENT.PLAYGROUND.TYPING')
                  : $t('AI_AGENT.PLAYGROUND.ONLINE')
              }}
            </span>
          </div>
          <StudioBadge tone="amber" class="ml-auto">
            <span class="i-lucide-flask-conical size-3" />
            {{ $t('AI_AGENT.PLAYGROUND.TEST_MODE') }}
          </StudioBadge>
        </header>
        <PlaygroundChat
          class="flex-1 min-h-0"
          :messages="messages"
          :is-sending="uiFlags.isSending"
          :bot-name="botName"
          :bot-avatar="bot.thumbnail"
          @send="send"
        />
      </section>
      <section
        class="p-4 lg:col-span-2 lg:sticky lg:top-4 lg:max-h-[36rem] lg:overflow-y-auto rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <TraceTimeline :trace="trace" />
      </section>
    </div>
  </div>
</template>
